import React from 'react';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';

const Pagination = ({
  page = 1,
  setPage,
  rows = 10,
  setRows,
  totalDocuments = 0,
}) => {
  const totalPages = Math.ceil(totalDocuments / rows);
  const currentPage = Math.min(Math.max(page, 1), Math.max(totalPages, 1));

  const startRecord = totalDocuments === 0 ? 0 : (currentPage - 1) * rows + 1;

  const endRecord = Math.min(currentPage * rows, totalDocuments);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    startPage = Math.max(1, endPage - maxVisiblePages + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const buttonClass =
    'inline-flex h-9 min-w-9 items-center justify-center rounded-md border text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

  const activeClass =
    'border-blue-600 bg-blue-600 text-white hover:bg-blue-700';

  const inactiveClass =
    'border-gray-300 bg-white text-gray-700 hover:bg-gray-50';

  const disabledClass =
    'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400';

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) {
      return;
    }

    setPage(nextPage);
  };

  const handleRowsChange = (event) => {
    const nextRows = Number(event.target.value);

    setRows(nextRows);
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-4 border-t border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Record information */}
      <div className="text-sm text-gray-600">
        Showing <span className="font-medium text-gray-900">{startRecord}</span>
        {' – '}
        <span className="font-medium text-gray-900">{endRecord}</span>
        {' of '}
        <span className="font-medium text-gray-900">
          {totalDocuments.toLocaleString()}
        </span>
        {' results'}
      </div>

      {/* Pagination controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end">
        {/* Page size */}
        <div className="flex items-center gap-2">
          <label htmlFor="pagination-rows" className="text-sm text-gray-600">
            Rows per page
          </label>

          <select
            id="pagination-rows"
            value={rows}
            onChange={handleRowsChange}
            className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Page navigation */}
        <nav aria-label="Pagination" className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1 || totalPages === 0}
            aria-label="Go to previous page"
            className={`${buttonClass} ${
              currentPage <= 1 || totalPages === 0
                ? disabledClass
                : inactiveClass
            }`}
          >
            <RiArrowLeftSLine size={20} aria-hidden="true" />
          </button>

          {getPageNumbers().map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              onClick={() => goToPage(pageNumber)}
              disabled={pageNumber === currentPage}
              aria-label={`Go to page ${pageNumber}`}
              aria-current={pageNumber === currentPage ? 'page' : undefined}
              className={`${buttonClass} ${
                pageNumber === currentPage ? activeClass : inactiveClass
              }`}
            >
              {pageNumber}
            </button>
          ))}

          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages || totalPages === 0}
            aria-label="Go to next page"
            className={`${buttonClass} ${
              currentPage >= totalPages || totalPages === 0
                ? disabledClass
                : inactiveClass
            }`}
          >
            <RiArrowRightSLine size={20} aria-hidden="true" />
          </button>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;
